import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import triggerAnalyticsEvent from 'syf-js-utilities/helpers/triggerAnalyticsEvent';
import { useTranslation } from 'react-i18next';
import { VerticalCardLoader } from 'syf-component-library/ui/atoms';
import { cliAnalyticsModalDefault } from '../../../../const/analytics';
import i18n from '../../strings/i18n';
import { CreditLimitIncreaseInput, initialCreditLimitIncreaseModalState } from '../../typings';
import CreditLimitIncreaseFooter from '../footer';
import useAccountCreditLimitChange from '../../../../services/account-manager/useAccountCreditLimitChange';
import { ConfigContext } from '../../context';
import StatusMessageHandler, { getImages } from '../utils/StatusMessageHandler';
import { useAccountSummary } from '../../../../const/syf-api-queries';
import { ErrorMessage } from '../utils/StatusMessage';
import CardDetails from '../../card-details-header';
import { BodyContainer, Divider, ModalContentWrapper } from '../subcomponents';
import RequestLimitIncrease from '../../request-limit-increase';
const CreditLimitIncreaseContent = () => {
    const { selectedAccountId, config, handleClose } = useContext(ConfigContext);
    const { t: translate } = useTranslation(undefined, { i18n });
    const [isError, setIsError] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [CLIState, setCLIState] = useState(initialCreditLimitIncreaseModalState);
    const { data: accountSummaryData, isError: isAccountSummaryError, isFetching: isAccountSummaryFetching, refetch } = useAccountSummary(selectedAccountId);
    const accountSummaryCreditLimit = accountSummaryData?.credit?.creditLimit;
    const hasAccountSummaryData = !isAccountSummaryFetching &&
        Boolean(accountSummaryData?.accountInfo?.accountLast4);
    const { data: creditLimitIncreaseData, isLoading: isCreditLimitIncreaseLoading, isError: isCreditLimitIncreaseError, isSuccess: isCreditLimitIncreaseSuccess, mutate } = useAccountCreditLimitChange({
        config,
        keyProps: {
            income: Number(CLIState.annualNetIncome.value),
            requestedLimit: Number(CLIState.desiredAmount.value),
            accountId: selectedAccountId
        }
    });
    const setInputError = (type, errorMessage) => setCLIState((prev) => ({
        ...prev,
        [type]: {
            value: prev[type].value,
            errorMessage
        }
    }));
    const fetchLimitIncrease = () => mutate({
        requestedLimit: Number(CLIState.desiredAmount.value),
        annualNetIncome: Number(CLIState.annualNetIncome.value),
        'cipher.accountId': selectedAccountId
    });
    const handleCancel = () => {
        handleClose();
        setIsError(false);
        setIsSuccess(false);
        setCLIState(initialCreditLimitIncreaseModalState);
    };
    // eslint-disable-next-line consistent-return
    const handleBlur = ({ type, value }) => {
        const newRequestedAmount = Number(value);
        if (!newRequestedAmount) {
            const errorMessage = type === CreditLimitIncreaseInput.annualNetIncome
                ? translate('modal.annualNetIncomeInputErrorText')
                : translate('modal.desiredIncreaseInputErrorText');
            triggerAnalyticsEvent({
                ...cliAnalyticsModalDefault,
                errorMessage
            });
            return setInputError(type, errorMessage);
        }
        if (type === CreditLimitIncreaseInput.desiredAmount) {
            const minRequestAmount = Number(accountSummaryCreditLimit || '') + 50;
            let errorMessage = '';
            if (newRequestedAmount < minRequestAmount) {
                errorMessage = translate('modal.increaseLimitByMore');
                triggerAnalyticsEvent({
                    ...cliAnalyticsModalDefault,
                    errorMessage
                });
            }
            setInputError(type, errorMessage);
        }
        else {
            setInputError(type, translate(''));
        }
    };
    const updateState = ({ type, event }) => setCLIState((prev) => ({
        ...prev,
        [type]: {
            errorMessage: prev[type].errorMessage,
            value: event.target.value
        }
    }));
    const isFormDataValid = () => {
        const hasErrors = Boolean(CLIState.desiredAmount.errorMessage ||
            CLIState.annualNetIncome.errorMessage);
        const isFormCompleted = Boolean(Number(CLIState.desiredAmount.value) &&
            Number(CLIState.annualNetIncome.value));
        return !isCreditLimitIncreaseLoading && !hasErrors && isFormCompleted;
    };
    const { error: errorImage } = getImages({ config });
    const isPrimaryDisabled = !isError && !isSuccess && !isAccountSummaryError && !isFormDataValid();
    const hasErrorOrSuccess = isError || isSuccess || isAccountSummaryError;
    const shouldRenderCardDetails = !isError && hasAccountSummaryData;
    const shouldRenderRequestLimitIncrease = !isError &&
        !isSuccess &&
        !isAccountSummaryError &&
        accountSummaryCreditLimit;
    useEffect(() => {
        if (refetch) {
            refetch();
        }
    }, [refetch]);
    useEffect(() => {
        setIsError(isCreditLimitIncreaseError && !hasAccountSummaryData);
        setIsSuccess(isCreditLimitIncreaseSuccess);
    }, [
        isCreditLimitIncreaseError,
        isCreditLimitIncreaseSuccess,
        hasAccountSummaryData
    ]);
    if (isAccountSummaryFetching || isCreditLimitIncreaseLoading) {
        return _jsx(VerticalCardLoader, {});
    }
    return (_jsxs(ModalContentWrapper, { children: [_jsx(ErrorBoundary, { fallback: _jsx(ErrorMessage, { id: "cli-error-boundary", image: errorImage }), children: _jsxs(BodyContainer, { children: [shouldRenderCardDetails && (_jsxs(_Fragment, { children: [_jsx(CardDetails, { productName: accountSummaryData?.product?.displayName, cardLast4: accountSummaryData?.accountInfo?.accountLast4, cardImage: accountSummaryData?.card?.cardArtURL }), _jsx(Divider, {})] })), shouldRenderRequestLimitIncrease ? (_jsx(RequestLimitIncrease, { ...{
                                creditLimitIncreaseState: CLIState,
                                creditLimit: accountSummaryCreditLimit,
                                handleBlur,
                                updateState
                            } })) : (_jsx(StatusMessageHandler, { ...{
                                isError: isError || isAccountSummaryError,
                                isSuccess,
                                creditLimitRequestResponse: creditLimitIncreaseData,
                                creditLimit: accountSummaryCreditLimit
                            } }))] }) }), _jsx(CreditLimitIncreaseFooter, { isPrimaryDisabled: isPrimaryDisabled, isSecondaryDisabled: isCreditLimitIncreaseLoading, hasErrorOrSuccess: hasErrorOrSuccess, fetchLimitIncrease: fetchLimitIncrease, handleCancel: handleCancel })] }));
};
export default CreditLimitIncreaseContent;
